import { makeStyles } from "@mui/styles";
import { Button } from "components/Button/Button";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { Icon } from "components/Icon/Icon";
import { centeredPopoverRootStyle, Popover } from "components/Popover/Popover";
import Tooltip from "components/Tooltip/Tooltip";
import { ReactNode, useState } from "react";
import styles from "./ClearsOnChangeLock.module.scss";

const useStyles = makeStyles(() => ({
    rootStyle: centeredPopoverRootStyle,
}));

/** Join labels as "A", "A and B", "A, B and C". */
export const joinLabels = (labels: string[]): string =>
    labels.length <= 1
        ? labels.join("")
        : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;

type ClearsOnChangeLockProps = {
    /** Label of the locked field, e.g. "Host". */
    label: string;
    /** Labels of the fields that are cleared when the locked field changes. */
    clearedLabels: string[];
    /** Called after the user confirms: clear the fields and unlock this one. */
    onUnlock: () => void;
    /** The field, rendered read-only. */
    children: ReactNode;
};

/**
 * Wraps a field whose schema carries `clears_on_change` on an existing record.
 * The field stays read-only until the user clicks the pen and confirms that
 * changing it clears the listed fields (e.g. changing a connector's host clears
 * its stored authentication, so a stored password never goes to a new
 * destination). The backend enforces the same rule.
 */
export const ClearsOnChangeLock = ({
    label,
    clearedLabels,
    onUnlock,
    children,
}: ClearsOnChangeLockProps) => {
    const classes = useStyles();
    const [isOpen, setIsOpen] = useState(false);
    const cleared = joinLabels(clearedLabels);
    return (
        <div className={styles.lock} data-testid="clears-on-change-lock">
            <div className={styles.lock__field}>{children}</div>
            <Tooltip overlay={`Change ${label}`}>
                <button
                    type="button"
                    className={styles.lock__pen}
                    aria-label={`Change ${label}`}
                    onClick={() => setIsOpen(true)}
                >
                    <Icon byName="pencilOutline" />
                </button>
            </Tooltip>
            <Popover
                anchorReference="none"
                contentClassName={styles.dialog}
                closeOnInsideClick={false}
                isOpen={isOpen}
                classes={{ root: classes.rootStyle }}
            >
                <div className={styles.dialog__header}>{`Change ${label}?`}</div>
                <div>{`Changing ${label} clears ${cleared}. Enter ${cleared} again for the new value.`}</div>
                <div className={styles.dialog__buttons}>
                    <CancelButton onClick={() => setIsOpen(false)} />
                    <Button
                        variation="primary"
                        label={`Change ${label}`}
                        onClick={() => {
                            setIsOpen(false);
                            onUnlock();
                        }}
                    />
                </div>
            </Popover>
        </div>
    );
};
